import {
  getAllSetting,
  getSettingByType,
  createSetting,
  upsertSetting,
} from "../APICalling/settingApi";
import {
  getShopifyWebhooks,
  createShopifyWebhook,
  deleteShopifyWebhook,
} from "../APICalling/shopifyApi";
import {
  sampleCRMMappings,
  sampleCRMRelatedListMappings,
} from "../sample_json/sample_mapping/samplemapping";
import { sampleWebhookBody } from "../sample_json/sample_mapping/samplewebhook";
import {
  EXTENSION_IDENTIFIER,
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
} from "../config/serviceConstant";

const MAPPING_BY_SERVICE = {
  "Zoho CRM": sampleCRMMappings,
};

// RelatedList settings are CRM-widget-specific; only CRM has them.
const RELATED_LIST_MAPPING_BY_SERVICE = {
  "Zoho CRM": sampleCRMRelatedListMappings,
};

// Serialise all calls so concurrent invocations never snapshot stale state.
let _lock = Promise.resolve();

/**
 * Ensures default field-mapping settings and Shopify webhooks exist
 * for the given service. Skips anything that already exists.
 *
 * @param {object}  settingAuth     - { orgId, apiKey, dataCenterUrl }
 * @param {object}  shopifyHeaders  - headers for Shopify API calls
 * @param {string}  shop            - e.g. "store.myshopify.com"
 * @param {string}  zapikey         - Zoho API key for webhook callback URL
 * @param {string}  service         - "Zoho CRM"
 * @returns {{ created, skipped, warnings }}
 */
export const ensureDefaultMappingsAndWebhooks = (
  settingAuth,
  shopifyHeaders,
  shop,
  zapikey,
  service
) => {
  // Queue behind any in-flight invocation so the next run always sees
  // settings/webhooks created by the previous one.
  const ticket = _lock.then(() =>
    _ensureDefaultMappingsAndWebhooksImpl(
      settingAuth,
      shopifyHeaders,
      shop,
      zapikey,
      service
    )
  );
  // Keep the chain alive even if this run fails.
  _lock = ticket.catch(() => {});
  return ticket;
};

const _ensureDefaultMappingsAndWebhooksImpl = async (
  settingAuth,
  shopifyHeaders,
  shop,
  zapikey,
  service
) => {
  const getMappings = MAPPING_BY_SERVICE[service];
  if (!getMappings) return { created: [], skipped: [], warnings: [] };

  const defaultMappings = getMappings(settingAuth.orgId, shop);
  const [allSettingsRaw, allWebhooksRaw] = await Promise.all([
    getAllSetting(settingAuth),
    getShopifyWebhooks(shopifyHeaders),
  ]);
  // Defensive: never let a non-array response abort the whole setup. Setting
  // creation must proceed even if the webhook list comes back malformed.
  const allSettings = Array.isArray(allSettingsRaw) ? allSettingsRaw : [];
  const allWebhooks = Array.isArray(allWebhooksRaw) ? allWebhooksRaw : [];

  // Scope existing settings to THIS shop only, so per-store loops don't
  // confuse another store's setting for "already exists".
  const existingSettings = (allSettings || []).filter((s) => {
    let sSchema = s.settingSchema || s.setting_schema || {};
    if (typeof sSchema === "string") {
      try { sSchema = JSON.parse(sSchema); } catch { sSchema = {}; }
    }
    return sSchema?.shop === shop;
  });

  // Scope webhooks to this shop via either its setting_ids or the shopifyshopurl query param
  const thisStoreSettingIds = new Set(
    existingSettings
      .map((s) => s.setting_id || s.settingId || s.id)
      .filter(Boolean)
  );
  const webhooksForThisShop = (allWebhooks || []).filter((wh) => {
    const addr = wh.address || "";
    const sidMatch = addr.match(/setting_id=([^&]+)/);
    const sid = sidMatch?.[1];
    if (sid && thisStoreSettingIds.has(sid)) return true;
    return addr.includes(`shopifyshopurl=${encodeURIComponent(shop)}`);
  });

  const created = [];
  const skipped = [];
  const warnings = [];

  // ── Orphan webhook cleanup ──
  // If a webhook for this shop points at a setting_id that doesn't match any
  // existing setting, it's a stale leftover from a previous revoke/uninstall
  // that didn't fully clean up. Delete it up front so the create loop below
  // doesn't end up with duplicate webhooks for the same topic.
  //
  // A webhook whose callback host is NOT the org's own Zoho data center is
  // treated as an orphan too — legacy builds hard-coded www.zohoapis.com, so
  // e.g. a Canada org's webhooks would call the US DC where its CRM functions
  // don't exist. Deleting it here lets the create loop below re-register it
  // on the correct host (sampleWebhookBody now uses dataCenterUrl).
  let expectedOrigin = "";
  try {
    if (settingAuth.dataCenterUrl) {
      expectedOrigin = new URL(settingAuth.dataCenterUrl).origin;
    }
  } catch {
    expectedOrigin = "";
  }
  const isWrongDcWebhook = (wh) => {
    if (!expectedOrigin) return false;
    try {
      return new URL(wh.address || "").origin !== expectedOrigin;
    } catch {
      return false;
    }
  };

  const orphanWebhooks = webhooksForThisShop.filter((wh) => {
    const addr = wh.address || "";
    const sidMatch = addr.match(/setting_id=([^&]+)/);
    const sid = sidMatch?.[1];
    if (sid && !thisStoreSettingIds.has(sid)) return true;
    return isWrongDcWebhook(wh);
  });
  if (orphanWebhooks.length > 0) {
    await Promise.all(
      orphanWebhooks.map((wh) => {
        const whId = wh.id || wh.webhook_id;
        const addr = wh.address || "";
        const sidMatch = addr.match(/setting_id=([^&]+)/);
        const sid = sidMatch?.[1] || "";
        if (!whId) return Promise.resolve();
        return deleteShopifyWebhook(shopifyHeaders, whId, sid).catch((e) => {
          warnings.push(
            `Failed to delete orphan webhook (topic: ${wh.topic || "unknown"}, id: ${whId}): ${e?.message || e}`
          );
        });
      })
    );
  }

  // Remove the deleted orphans so downstream checks don't see them
  const orphanIds = new Set(orphanWebhooks.map((wh) => wh.id || wh.webhook_id));
  const existingWebhooks = webhooksForThisShop.filter(
    (wh) => !orphanIds.has(wh.id || wh.webhook_id)
  );

  // Track all setting IDs we know about for this store (existing + newly created)
  const knownSettingIds = new Set(thisStoreSettingIds);

  for (const mapping of defaultMappings) {
    const moduleName = mapping.module_name;
    const schema = mapping.setting_schema;
    const mappingTrigger = schema.triggerApiName;

    // Match on module + service + trigger so multiple triggers for the same
    // module (e.g. customers/create, customers/update, customers/delete) each
    // get their own setting.
    const existing = existingSettings.find((s) => {
      let sSchema = s.settingSchema || s.setting_schema || {};
      if (typeof sSchema === "string") {
        try { sSchema = JSON.parse(sSchema); } catch { sSchema = {}; }
      }
      const sModule = s.moduleApiName || s.module_name || sSchema.module;
      const sService = sSchema.service;
      const sTrigger = sSchema.triggerApiName;
      return sModule === moduleName && sService === service && sTrigger === mappingTrigger;
    });

    const label = `${service} - ${moduleName} [${mappingTrigger}]`;
    let settingId;
    let existingEnabled = false;

    if (existing) {
      settingId = existing.setting_id || existing.settingId || existing.id;
      let sSchema = existing.settingSchema || existing.setting_schema || {};
      if (typeof sSchema === "string") {
        try { sSchema = JSON.parse(sSchema); } catch { sSchema = {}; }
      }
      existingEnabled = sSchema?.enabled === true;
      skipped.push(`${label} (setting exists)`);
    } else {
      // Create the setting
      const result = await createSetting(settingAuth, {
        settingSchema: schema,
        moduleApiName: moduleName,
        settingType: mapping.setting_type,
      });

      if (!result) {
        warnings.push(`Failed to create setting for ${label}`);
        continue;
      }

      settingId = result.setting_id || result.settingId || result.id;
      if (settingId) knownSettingIds.add(settingId);
      created.push(`${label} (setting created)`);
    }

    if (!settingId) continue;

    // For mandatory triggers (CUSTOMERS_CREATE / ORDERS_CREATE /
    // PRODUCTS_CREATE), the sample mapping declares
    // enabled: true but the backend's create endpoint may persist it as
    // disabled. Force-upsert with enabled: true so the toggle reflects the
    // intent for a freshly-connected store.
    //
    // EXISTING CRM2Shopify rules are exempt: their enabled state belongs to
    // the Dashboard's "Both Way Sync" master toggle — re-activating one here
    // would silently override a user who turned two-way sync off. Freshly
    // created ones (!existing) still get the activation fix-up.
    const activationAllowed =
      !existing || schema?.direction !== "CRM2Shopify";
    if (schema?.enabled === true && !existingEnabled && activationAllowed) {
      const activated = await upsertSetting(settingAuth, {
        settingId,
        settingSchema: schema,
        moduleApiName: moduleName,
        settingType: mapping.setting_type,
      });
      if (activated) {
        created.push(`${label} (activated)`);
      } else {
        warnings.push(`Failed to activate ${label}`);
      }
    }

    // CRM → Shopify rules are fired by Zoho workflows, not Shopify webhooks —
    // never register a webhook for them.
    if (schema?.direction === "CRM2Shopify") continue;

    // Check if a webhook already exists for this setting
    const webhookExists = existingWebhooks.some((wh) => {
      const addr = wh.address || "";
      return addr.includes(`setting_id=${settingId}`);
    });

    if (webhookExists) {
      if (!existing) {
        // Setting was just created but webhook somehow existed — suspicious
        skipped.push(`${label} (webhook already exists)`);
      }
      continue;
    }

    // Create webhook — for optional (disabled) settings the webhook is still
    // registered with Shopify so events start queuing immediately once the
    // user flips the setting on, with no further provisioning needed.
    // The callback URL must target the merchant's own Zoho data center
    // (e.g. www.zohoapis.ca for Canada), so pass the org's dataCenterUrl.
    const body = {
      ...sampleWebhookBody(
        settingId,
        zapikey,
        mappingTrigger,
        shop,
        settingAuth.dataCenterUrl
      ),
      topic: mappingTrigger,
    };
    const whResult = await createShopifyWebhook(shopifyHeaders, body);
    if (whResult) {
      created.push(`${label} (webhook created)`);
    } else {
      warnings.push(`Failed to create webhook for ${label}`);
    }
  }

  // ── RelatedList settings (CRM widget, not shop-scoped) ───────────────
  // One per module regardless of shop count. No webhooks involved.
  // Fetched explicitly by settingType because getAllSetting may not include
  // relatedList entries (backend default can filter to systemDefined).
  const getRelatedListMappings = RELATED_LIST_MAPPING_BY_SERVICE[service];
  if (getRelatedListMappings) {
    const relatedListMappings = getRelatedListMappings(settingAuth.orgId);
    const existingRelatedList = await getSettingByType(settingAuth, "relatedList");
    // Track modules we've already created in THIS run so two mappings for the
    // same module can't both slip through.
    const seenModules = new Set(
      (existingRelatedList || [])
        .map((s) => s.moduleApiName || s.module_name)
        .filter(Boolean)
    );

    for (const mapping of relatedListMappings) {
      const moduleName = mapping.module_name;
      const label = `${service} - ${moduleName} [relatedList]`;

      if (seenModules.has(moduleName)) {
        skipped.push(`${label} (setting exists)`);
        continue;
      }

      const result = await createSetting(settingAuth, {
        settingSchema: mapping.setting_schema,
        moduleApiName: moduleName,
        settingType: mapping.setting_type,
      });
      if (result) {
        seenModules.add(moduleName);
        created.push(`${label} (setting created)`);
      } else {
        warnings.push(`Failed to create setting for ${label}`);
      }
    }
  }

  return { created, skipped, warnings };
};

/**
 * Idempotent safety-net: ensure default mappings + webhooks exist for every
 * connected store across the given Zoho services.
 *
 * Why this exists: the per-event triggers (Shopify post-install in
 * ShopifyStoresTab, Zoho post-auth polling in ZohoAuthTab) only fire in narrow
 * windows. ZohoAuthTab in particular unmounts as soon as both connections are
 * live (SettingsRoute auto-navigates to the Dashboard), so its on-mount
 * `runDefaultSetup` never runs again — and if the connect-time OAuth poll timed
 * out, defaults were never created at all. SettingsRoute calls this on load so
 * defaults always get created once both Shopify and Zoho are connected.
 *
 * Safe to call repeatedly and concurrently: every store/service run goes
 * through ensureDefaultMappingsAndWebhooks, which skips anything that already
 * exists and serialises behind the shared lock.
 *
 * @param {object}   opts
 * @param {string}   opts.orgId
 * @param {string}   opts.apiKey
 * @param {string}   opts.dataCenterUrl
 * @param {string}   opts.dataCenter
 * @param {string}   opts.zapikey
 * @param {string[]} opts.storeDomains  - e.g. ["store.myshopify.com", …]
 * @param {string[]} opts.services      - defaults to ["Zoho CRM"]
 * @returns {{ created, skipped, warnings }}
 */
export const ensureDefaultsForAllStores = async ({
  orgId,
  apiKey,
  dataCenterUrl,
  dataCenter,
  zapikey,
  storeDomains = [],
  services = ["Zoho CRM"],
}) => {
  const created = [];
  const skipped = [];
  const warnings = [];

  for (const storeDomain of storeDomains) {
    if (!storeDomain) continue;
    const shopifyHeaders = {
      orgid: orgId,
      connname: EXTENSION_IDENTIFIER,
      apikey: apiKey,
      datacenterurl: dataCenterUrl,
      datacenter: dataCenter,
      [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(storeDomain),
    };
    const settingAuth = { orgId, apiKey, dataCenterUrl, shop: storeDomain };

    for (const service of services) {
      try {
        const res = await ensureDefaultMappingsAndWebhooks(
          settingAuth,
          shopifyHeaders,
          storeDomain,
          zapikey,
          service
        );
        created.push(...(res?.created || []));
        skipped.push(...(res?.skipped || []));
        warnings.push(...(res?.warnings || []));
      } catch (e) {
        warnings.push(`${storeDomain} / ${service}: ${e?.message || e}`);
      }
    }
  }

  return { created, skipped, warnings };
};
